import React, { useState, useEffect } from 'react';

const AlertModal = ({ isOpen, onClose, message }) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Trigger entrance animation after a brief delay
      setTimeout(() => setIsAnimating(true), 50);
    } else {
      setIsAnimating(false);
    }
  }, [isOpen]);

  const closeModal = () => {
    setIsAnimating(false);
    setTimeout(() => onClose(), 300);
  };

  if (!isOpen || !message) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with fade animation */}
      <div
        className={`fixed inset-0 bg-gradient-to-br from-red-900/40 via-orange-900/40 to-rose-900/40 backdrop-blur-sm transition-opacity duration-300 ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={closeModal}
      />

      {/* Modal with scale and slide animation */}
      <div
        className={`relative bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl max-w-lg w-full z-10 overflow-hidden transition-all duration-300 transform ${
          isAnimating ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 translate-y-4 opacity-0'
        }`}
      >
        {/* Animated gradient border */}
        <div className="absolute inset-0 bg-gradient-to-r from-red-500 via-orange-500 to-rose-500 opacity-75 blur-xl animate-pulse" />

        {/* Content container */}
        <div className="relative bg-white rounded-2xl m-[2px]">
          {/* Decorative top bar with gradient */}
          <div className="h-2 bg-gradient-to-r from-red-500 via-orange-500 to-rose-500 animate-gradient-x" />

          {/* Header */}
          <div className="p-6 pb-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                {/* Animated icon with pulse effect */}
                <div className="relative">
                  <div className="absolute inset-0 bg-red-400 rounded-full animate-ping opacity-75" />
                  <div className="relative bg-gradient-to-br from-red-500 to-orange-600 rounded-full p-3 shadow-lg">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                    Important Notice
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">Please read carefully</p>
                </div>
              </div>

              {/* Close button with hover effect */}
              <button
                onClick={closeModal}
                className="group relative p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-pink-500 rounded-full opacity-0 group-hover:opacity-10 transition-opacity duration-200" />
                <svg
                  className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Content with gradient border */}
          <div className="px-6 pb-6">
            <div className="relative bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-5 border border-red-100 shadow-inner">
              {/* Decorative corner elements */}
              <div className="absolute top-0 left-0 w-16 h-16 bg-gradient-to-br from-red-400/20 to-transparent rounded-tl-xl" />
              <div className="absolute bottom-0 right-0 w-16 h-16 bg-gradient-to-tl from-orange-400/20 to-transparent rounded-br-xl" />

              <p className="relative text-gray-700 leading-relaxed whitespace-pre-wrap">
                {message}
              </p>
            </div>
          </div>

          {/* Footer with gradient button */}
          <div className="px-6 pb-6">
            <button
              onClick={closeModal}
              className="group relative w-full py-3 px-6 rounded-xl font-semibold text-white shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
            >
              {/* Animated gradient background */}
              <div className="absolute inset-0 bg-gradient-to-r from-red-600 via-orange-600 to-rose-600 transition-transform duration-300 group-hover:scale-110" />

              {/* Shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-30 transition-opacity duration-500 transform -skew-x-12 group-hover:translate-x-full" />

              {/* Button text */}
              <span className="relative flex items-center justify-center space-x-2">
                <span>Got it!</span>
                <svg
                  className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertModal;
